/**
 * AI Assistant Service
 *
 * Service layer for AI assistant communication.
 * Two implementations:
 *   - createPlaceholderService() — static "not ready" response, for dev/testing
 *   - createLLMService(backend) — real backend integration via client library
 *
 * With createLLMService, sendMessage POSTs via the AddUserMessage aggregate
 * command and resolves immediately. The actual assistant response content
 * arrives separately via WebSocket streaming, processed by the backendPolicy.
 */

import { AIAssistantResponse, AIPromptRequest, ConversationSummary } from './types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';

/**
 * One persisted message of a conversation, as the per-message readmodel projects
 * it (TV1-2382 P7).
 *
 * Declared LOCALLY and deliberately. The canonical shape lives in
 * `teselly-domain`'s `ConversationMessageReadmodelSchema`, but the web-app must
 * not import a backend/shared domain package for a wire type — the same
 * reasoning `domains/aiAssistant/state/rulePreview.ts` records for
 * `CandidateGraph`. A drift between the two shows up as a block that fails to
 * narrow and renders as plain text, never as a crash.
 *
 * `block` is what makes an agreed draft-decomposition card survive a reload.
 */
export interface StoredChatMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  type?: string;
  block?: { type: string; payload: unknown };
  createdAt?: string;
}

export interface AIAssistantService {
  sendMessage( params: AIPromptRequest ): Promise<AIAssistantResponse>;
  createConversation(): Promise<{ id: string }>;
  getConversations(): Promise<ConversationSummary[]>;
  /**
   * Full message history for one conversation, artifacts included — the read
   * side of the P7 durability change. Used to REHYDRATE the chat on load so an
   * agreed automation draft is still there after a refresh.
   */
  getMessages( conversationId: string ): Promise<StoredChatMessage[]>;
  /**
   * Rate one assistant answer (TV1-2417). Resolves `true` only when the backend
   * confirms the score reached the observability sink.
   *
   * NEVER REJECTS. Feedback is telemetry the seller volunteered; a failure to
   * record it is not her problem and must not become an error on her screen — least
   * of all on a thumbs-DOWN, which is the rating this feature exists to collect.
   */
  sendFeedback( params: { traceId: string; rating: 'up' | 'down' }): Promise<boolean>;
}

/**
 * Real LLM service — sends messages to the backend via the client library.
 *
 * - sendMessage: POSTs via backend.commands.AddUserMessage (aggregate command).
 *   Resolves with empty content — the real assistant reply arrives via WebSocket
 *   streaming and is handled by backendPolicy.
 *
 * - createConversation: generates a uuidv7 client-side. The Conversations
 *   aggregate is created on the first AddUserMessage with that ID (PV3 pattern).
 *
 * - getConversations: reads `ConversationsReadmodel` through
 *   `queries.AiAssistantConversations` (GET /ai-assistant/conversations).
 *   The readmodel has no `title` column, so the title is derived from
 *   `lastMessage` — see `toConversationSummary`.
 */
export function createLLMService( backend: ClientLibraryType ): AIAssistantService {
  return {
    async sendMessage( params: AIPromptRequest ): Promise<AIAssistantResponse> {
      const messageId = uuidv7();

      // POST to the Conversations aggregate via client library.
      // This triggers the backend event-sourced flow:
      //   AddUserMessage -> UserMessageAdded event -> trigger policy -> StartAgentRun
      // The actual response content streams back via WebSocket.
      await backend.commands.AddUserMessage({
        id: params.conversationId,
        body: {
          message: {
            content: params.message,
            type: 'text'
          },
          context: {
            route: params.context.route,
            storeStatesSummary: params.context.storeStates,
            locale: params.context.locale
          }
        }
      });

      // Return stub response — content will be populated via WebSocket streaming
      return {
        content: '',
        conversationId: params.conversationId,
        messageId
      };
    },

    async createConversation(): Promise<{ id: string }> {
      // PV3 aggregates use client-generated IDs. The aggregate is created on
      // first AddUserMessage, so we never call the backend conversation
      // endpoint from here.
      return { id: uuidv7() };
    },

    async getConversations(): Promise<ConversationSummary[]> {
      // PV3 readmodel queries resolve to a FLAT array, not `{ data, count }`.
      const rows = await backend.queries.AiAssistantConversations();
      const items = Array.isArray( rows ) ? rows : [];
      return items
        .map( toConversationSummary )
        .sort(( a, b ) => b.updatedAt - a.updatedAt );
    },

    async getMessages( conversationId: string ): Promise<StoredChatMessage[]> {
      // The query method is GENERATED from the readmodel's route, so it exists
      // only in a client library built after `yarn generate-all`. Reached
      // defensively and degrading to an EMPTY history rather than throwing: a
      // client build that predates the route must render an empty chat, not a
      // crashed panel. It is not silent — the drift shows up as a conversation
      // that will not rehydrate, which is diagnosable; a thrown query is not.
      const queries = backend.queries as unknown as Record<string, ( args?: unknown ) => Promise<unknown>>;
      const query = queries.AiAssistantConversationMessages;
      if ( typeof query !== 'function' ) return [];

      const rows = await query({ conversationId });
      const items = Array.isArray( rows ) ? rows : [];
      return items
        .map( toStoredChatMessage )
        .sort(( a, b ) => ( a.createdAt ?? '' ).localeCompare( b.createdAt ?? '' ));
    },

    async sendFeedback({ traceId, rating }): Promise<boolean> {
      // Reached DEFENSIVELY through the generated `post` surface, the same way
      // `getMessages` reaches its generated query and for the same reason: the method
      // exists only in a client library built after `yarn generate-all`, and a build
      // that predates the route must degrade rather than throw inside a click handler.
      const posts = backend.post as unknown as Record<string, ( body: unknown ) => Promise<unknown>>;
      const post = posts.ai_assistant_feedback;
      if ( typeof post !== 'function' ) return false;

      try {
        const response = await post({ traceId, rating }) as { recorded?: boolean } | undefined;
        return response?.recorded === true;
      } catch {
        // Swallowed on purpose — see the port docblock. The rating is already shown
        // as given; there is nothing useful to tell the seller about a telemetry write.
        return false;
      }
    }
  };
}

/**
 * Narrow one readmodel row. Shape-narrowing only — the backend already decided
 * what a message is. An unrecognised `block` is carried through untouched: the
 * `messageBlocks` registry ignores a type this build does not know, which is the
 * documented degrade-to-plain-message behaviour.
 */
function toStoredChatMessage( row: any ): StoredChatMessage {
  return {
    id: String( row?.id ?? '' ),
    conversationId: String( row?.conversationId ?? '' ),
    role: row?.role === 'assistant' ? 'assistant' : 'user',
    content: typeof row?.content === 'string' ? row.content : '',
    type: typeof row?.type === 'string' ? row.type : undefined,
    ...( row?.block && typeof row.block?.type === 'string' ? { block: row.block } : {}),
    createdAt: typeof row?.createdAt === 'string' ? row.createdAt : undefined
  };
}

/** Max characters kept when deriving a conversation title from its last message. */
const TITLE_MAX = 48;

/**
 * `ConversationsReadmodel` has no `title` column — it carries `lastMessage`,
 * `lastRole`, `messageCount` and timestamps. The sidebar needs a label, so one
 * is derived here rather than invented in the component: a single place to
 * change if the backend ever projects a real title.
 */
function toConversationSummary( row: any ): ConversationSummary {
  const lastMessage = typeof row?.lastMessage === 'string' ? row.lastMessage : '';
  const collapsed = lastMessage.replace( /\s+/g, ' ' ).trim();
  const title = collapsed.length > TITLE_MAX
    ? `${ collapsed.slice( 0, TITLE_MAX ).trimEnd() }…`
    : collapsed;

  // `updatedAt` is an ISO string on the readmodel; the FE summary is epoch ms.
  const updatedAtRaw = row?.updatedAt ?? row?.createdAt;
  const parsed = updatedAtRaw ? Date.parse( updatedAtRaw ) : NaN;

  return {
    id: String( row?.id ?? '' ),
    title,
    lastMessage: collapsed,
    updatedAt: Number.isNaN( parsed ) ? 0 : parsed,
    messageCount: typeof row?.messageCount === 'number' ? row.messageCount : 0
  };
}

/**
 * Placeholder implementation — returns a static "not ready" message.
 * No command plans, no keyword matching, no side effects.
 * Kept for development and testing fallback.
 */
export function createPlaceholderService(): AIAssistantService {
  let conversationCounter = 0;

  return {
    async sendMessage( params: AIPromptRequest ): Promise<AIAssistantResponse> {
      return {
        content: 'The AI assistant is not ready yet. This feature is under development.',
        conversationId: params.conversationId,
        messageId: `msg-${Date.now()}-${Math.random().toString( 36 )
.slice( 2, 8 )}`
      };
    },

    async createConversation(): Promise<{ id: string }> {
      conversationCounter++;
      return { id: `conv-${conversationCounter}-${Date.now()}` };
    },

    async getConversations(): Promise<ConversationSummary[]> {
      return [];
    },

    async getMessages(): Promise<StoredChatMessage[]> {
      return [];
    },

    async sendFeedback(): Promise<boolean> {
      // Nothing to score — the placeholder never ran an agent, so no trace exists.
      return false;
    }
  };
}
